<template>
  <SLoadingOverlay
    :loading="loading"
    class="global-overlay"
    data-testid="custom-loading-overlay"
    :style="{ position: 'fixed', zIndex: 2147483738 }"
  />
</template>

<script lang="ts" setup>
// Port of Nuxt2's `components/base/CustomLoading.vue`, wired from nuxt.config's
// `loading` key. Nuxt2's App.js rendered it as a sibling of the layout inside
// #__nuxt — outside #__layout, so it also covered routes whose layout is bare
// (/cart and /checkout are just <NuxtPage /> in Nuxt4) — and drove it from
// client.js: `$loading.start()` in loadAsyncComponents once the route or params
// had changed, `$loading.finish()` once the destination's asyncData/fetch
// promises had settled.
//
// Nuxt4 dropped the `loading` config, so nothing covered a client-side
// navigation: the checkout page awaits `useAsyncData` in setup, so its invoice
// fetch blocks the route change and the cart page simply sat there, clickable
// and unchanged, until the page resolved. These are the same two moments Nuxt2
// used: the cover goes up as the navigation starts (the 00.route-cover global
// middleware, first in Nuxt's guard and before the destination chunk is fetched),
// and comes down on `page:loading:end`, when the destination's <Suspense> resolves,
// i.e. once a blocking `await useAsyncData` in the page setup has settled.
//
// Subscribing on mount, not in setup, mirrors Nuxt2, where `$loading` was only
// reachable from `mounted()`, and avoids a server-scope subscription per request.
// The middleware skips the hydrating navigation, so a hard load never flashes this
// over already-rendered SSR content.

const nuxtApp = useNuxtApp()

// Raised by the 00.route-cover global middleware, which decides per navigation (it knows
// `to` and `from`, and runs before any other middleware can hold the navigation open).
// AB#64327
const loading = useRouteCover()

const lower = (): void => {
  loading.value = false
}

const unsubscribers: Array<() => void> = []

onMounted(() => {
  unsubscribers.push(
    nuxtApp.hook('page:loading:end', lower),
    // Nuxt2 hid the loader from its errorChanged watcher. A page that throws
    // while suspending never reaches page:loading:end, and this overlay covers
    // the viewport, so without this the error page would render behind it.
    nuxtApp.hook('vue:error', lower)
  )
})

onBeforeUnmount(() => {
  unsubscribers.forEach(unsubscribe => unsubscribe())
})
</script>

<style lang="scss" scoped>
// Position and z-index are inline on the element above rather than declared
// here: the ported SLoadingOverlay sets `position: absolute` and
// `z-index: 2147483638` on that same element from its own scoped block, so a
// rule here would be an equal-specificity coin flip on build order. Nuxt2 won
// both with an `!important` on `.global-overlay` plus an inline
// `position: fixed` on CustomLoading; inline reproduces the result without the
// `!important`. BaseLayout declares the same z-index for its own rootLoading
// overlay — Nuxt2 had it in one unscoped block that served both.
.global-overlay {
  :deep(.v-progress-circular) {
    position: absolute;
    top: 50vh;
  }
}
</style>
